import { Component, Input, Output, EventEmitter, forwardRef, ViewChild, ElementRef, OnChanges, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export type CheckboxSize = 'small' | 'medium' | 'large';

@Component({
  selector: 'app-checkbox',
  templateUrl: './checkbox.component.html',
  styleUrls: ['./checkbox.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CheckboxComponent),
      multi: true
    }
  ]
})
export class CheckboxComponent implements ControlValueAccessor, OnChanges {
  @ViewChild('checkboxInput') checkboxInput!: ElementRef<HTMLInputElement>;

  @Input() checked: boolean = false;
  @Input() disabled: boolean = false;
  @Input() size: CheckboxSize = 'medium';
  @Input() label: string = '';
  @Input() indeterminate: boolean = false;
  @Input() error: boolean = false;

  checkboxId: string = `checkbox-${Math.random().toString(36).substr(2, 9)}`;

  @Output() checkedChange = new EventEmitter<boolean>();
  @Output() change = new EventEmitter<boolean>();

  private onChange: (value: boolean) => void = () => {};
  private onTouched: () => void = () => {};

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['indeterminate'] && this.checkboxInput) {
      this.updateIndeterminate();
    }
    if (changes['checked'] && this.checkboxInput) {
      this.checkboxInput.nativeElement.checked = this.checked;
    }
  }

  private updateIndeterminate(): void {
    if (this.checkboxInput) {
      this.checkboxInput.nativeElement.indeterminate = this.indeterminate;
    }
  }

  onCheckboxChange(event: Event): void {
    console.log('[Checkbox Component] Change event fired:', { checked: (event.target as HTMLInputElement).checked });
    const input = event.target as HTMLInputElement;
    this.checked = input.checked;
    this.indeterminate = false;
    this.updateIndeterminate();
    this.onChange(this.checked);
    this.checkedChange.emit(this.checked);
    this.change.emit(this.checked);
    console.log('[Checkbox Component] Emitted checkedChange:', this.checked);
  }

  writeValue(value: boolean): void {
    this.checked = value;
  }

  registerOnChange(fn: (value: boolean) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  getCheckboxClasses(): string {
    const classes = ['checkbox'];
    if (this.size) classes.push(`size-${this.size}`);
    if (this.checked) classes.push('checked');
    if (this.disabled) classes.push('disabled');
    if (this.error) classes.push('error');
    if (this.indeterminate) classes.push('indeterminate');
    return classes.join(' ');
  }

  getBoxClasses(): string {
    const classes = ['checkbox-box'];
    if (this.error) classes.push('error');
    return classes.join(' ');
  }
}
